import React, { useState, useRef, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { LuX } from "react-icons/lu";
import { ADMISSION_STATUS, STATUS_CONFIG } from "../utils";

const STATUS_OPTIONS = Object.values(ADMISSION_STATUS).map((value) => ({
  value,
  label: STATUS_CONFIG[value]?.label || value,
}));

const StatusLookup = ({ onChange, initialValues = [], placeholder = "Sélectionner des statuts" }) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedValues, setSelectedValues] = useState(initialValues);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const itemsRef = useRef([]);

  useEffect(() => {
    setSelectedValues(initialValues);
  }, [initialValues]);

  const available = STATUS_OPTIONS.filter((option) => !selectedValues.includes(option.value));
  const filtered =
    inputValue.trim() === "" ? available : available.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()));

  useEffect(() => {
    setSelectedIndex(filtered.length > 0 ? 0 : -1);
  }, [inputValue, selectedValues.length]);

  useEffect(() => {
    if (selectedIndex >= 0 && itemsRef.current[selectedIndex]) {
      itemsRef.current[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const addStatus = (option) => {
    const newSelected = [...selectedValues, option.value];
    setSelectedValues(newSelected);
    if (onChange) onChange(newSelected);
    setInputValue("");
    setSelectedIndex(-1);
    if (inputRef.current) inputRef.current.focus();
  };

  const removeStatus = (value) => {
    const newSelected = selectedValues.filter((v) => v !== value);
    setSelectedValues(newSelected);
    if (onChange) onChange(newSelected);
    if (inputRef.current) inputRef.current.focus();
  };

  const navOpts = { enabled: showDropdown === true && filtered.length > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1)), navOpts, [showDropdown, filtered]);
  useHotkeys("up", () => setSelectedIndex((i) => Math.max(i - 1, 0)), navOpts, [showDropdown, filtered]);
  useHotkeys("enter", () => addStatus(filtered[selectedIndex >= 0 ? selectedIndex : 0]), navOpts, [showDropdown, filtered, selectedIndex]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setShowDropdown(false);
      setSelectedIndex(-1);
      if (inputRef.current) inputRef.current.blur();
    } else if (e.key === "Backspace" && inputValue === "" && selectedValues.length > 0) {
      removeStatus(selectedValues[selectedValues.length - 1]);
    }
  };

  return (
    <div className="relative w-full">
      <div
        className="bg-white flex flex-wrap items-center gap-1 p-2 border rounded-lg transition-all duration-200 min-h-[42px] cursor-text"
        onClick={() => inputRef.current && inputRef.current.focus()}
      >
        {selectedValues.map((value) => (
          <div key={value} className="bg-primary-100 text-primary-800 px-1 py-0.5 rounded-md text-sm flex items-center">
            {STATUS_CONFIG[value]?.label || value}
            <button
              className="ml-1 text-primary-600 hover:text-primary-800"
              onClick={(e) => {
                e.stopPropagation();
                removeStatus(value);
              }}
              type="button"
              aria-label={`Supprimer ${STATUS_CONFIG[value]?.label || value}`}
            >
              <LuX className="h-4 w-4" />
            </button>
          </div>
        ))}
        <div className="flex items-center flex-grow">
          <input
            ref={inputRef}
            className="flex-grow outline-none min-w-[120px]"
            placeholder={selectedValues.length === 0 ? placeholder : ""}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowDropdown(true)}
            onBlur={() =>
              setTimeout(() => {
                setShowDropdown(false);
                setSelectedIndex(-1);
              }, 200)
            }
          />
        </div>
      </div>

      {showDropdown && filtered.length > 0 && (
        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto max-h-48">
          {filtered.map((option, idx) => (
            <div
              ref={(el) => (itemsRef.current[idx] = el)}
              key={option.value}
              className={`p-3 cursor-pointer flex justify-between items-center border-b border-gray-100 last:border-b-0 ${
                selectedIndex === idx ? "bg-primary-50 text-primary-700" : "hover:bg-gray-50"
              }`}
              onMouseDown={() => addStatus(option)}
            >
              <span className="font-medium">{option.label}</span>
              {selectedIndex === idx && <div className="text-primary-500 bg-primary-100 rounded px-2 py-1 text-xs font-medium">Enter</div>}
            </div>
          ))}
        </div>
      )}

      {showDropdown && filtered.length === 0 && (
        <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-gray-500 text-sm">Aucun statut trouvé</div>
      )}
    </div>
  );
};

export default StatusLookup;
